import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, DoCheck } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Event, BalanceTeamsResult } from '../../shared/models/event';
import { PlayerEventInfo, hasAssignedTeams } from '../../shared/models/playerEventInfo';
import { EventService } from '../../shared/services/event.service';
import { PlayerEventInfoService } from '../../shared/services/playereventinfo.service';
import { PlayerService } from '../../shared/services/player.service';

type Jersey = 'WHITE' | 'BLACK';

/** One persisted roster editor for home and match detail. Never balances on open. */
@Component({selector: 'app-team-board', standalone: false,
  templateUrl: './team-board.component.html', styleUrls: ['./team-board.component.css']})
export class TeamBoardComponent implements OnChanges, OnDestroy, DoCheck {
  @Input() event: Event;
  @Input() compact = false;
  @Input() disabled = false;
  @Output() rosterChanged = new EventEmitter<PlayerEventInfo[]>();
  @Output() player = new EventEmitter<PlayerEventInfo>();
  @Output() savingChange = new EventEmitter<boolean>();
  readonly jerseys: Jersey[] = ['WHITE', 'BLACK'];
  private saving = false;
  get busy(): boolean { return this.saving; }
  set busy(value: boolean) {
    if (this.saving === value) return;
    this.saving = value;
    this.savingChange.emit(value);
  }
  refreshRequired = false;
  error = '';
  confirmation: 'balance' | 'clear' | null = null;
  details = false;
  result: BalanceTeamsResult = null;
  private signature = '';
  private dragged: string = null;
  private destroyed = new Subject<void>();
  constructor(private events: EventService, private roster: PlayerEventInfoService, private players: PlayerService) {}
  ngOnChanges(): void {
    const signature = JSON.stringify([this.event?.id, this.confirmed.map(p => [p.player.id, p.jersey, p.rosterPosition, p.teamAssigned])]);
    if (this.signature !== signature) { this.result = null; this.details = false; this.signature = signature; }
  }
  ngOnDestroy(): void { this.destroyed.next(); this.destroyed.complete(); this.busy = false; }
  ngDoCheck(): void { this.ngOnChanges(); }
  get confirmed(): PlayerEventInfo[] {
    return (this.event?.playerEventInfos || []).filter(p => p.player?.id && p.status === 'CONFIRMED' && p.teamMemberType !== 'CANDIDATE');
  }
  team(jersey: Jersey): PlayerEventInfo[] {
    return this.confirmed.filter(p => p.jersey === jersey).sort((a, b) =>
      (a.rosterPosition ?? Number.MAX_SAFE_INTEGER) - (b.rosterPosition ?? Number.MAX_SAFE_INTEGER));
  }
  get assigned(): boolean {
    return hasAssignedTeams(this.confirmed);
  }
  get unassigned(): PlayerEventInfo[] { return this.confirmed.filter(p => !this.jerseys.includes(p.jersey as Jersey)); }
  get canManage(): boolean {
    return !!this.event?.viewedByManager && this.event.manager?.id === this.players.getCurrentPlayer()?.id &&
      this.event.active !== false && !this.event.cancelled && !this.event.incompleteSetup && !this.event.historyEvent;
  }
  get locked(): boolean { return this.disabled || this.busy || this.refreshRequired; }
  get visible(): boolean { return this.assigned || this.canManage && this.confirmed.length >= 2 || !this.compact; }
  ask(action: 'balance' | 'clear'): void {
    if (!this.canManage || this.locked || action === 'balance' && this.confirmed.length < 2) return;
    this.confirmation = action;
  }
  confirm(): void {
    const action = this.confirmation;
    this.confirmation = null;
    if (!action || !this.canManage || this.locked) return;
    this.run(action === 'balance' ? this.events.balanceTeams(this.event.id) : this.events.clearTeams(this.event.id), action === 'balance');
  }
  move(p: PlayerEventInfo, jersey: Jersey, position?: number): void {
    if (!this.canManage || this.locked || !this.confirmed.some(item => item.player.id === p.player.id)) return;
    const target = this.team(jersey).filter(item => item.player.id !== p.player.id);
    const index = Math.max(0, Math.min(position ?? target.length, target.length));
    if (p.jersey === jersey && this.team(jersey).indexOf(p) === index) return;
    this.run(this.events.moveBalancedPlayer(this.event.id, p.player.id, jersey, index));
  }
  drag(event: DragEvent, p: PlayerEventInfo): void {
    if (!this.canManage || this.locked) { event.preventDefault(); return; }
    this.dragged = p.player.id;
    event.dataTransfer?.setData('text/plain', p.player.id);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }
  dragEnd(): void { this.dragged = null; }
  allowDrop(event: DragEvent): void { if (this.dragged && this.canManage && !this.locked) event.preventDefault(); }
  drop(event: DragEvent, jersey: Jersey, position?: number): void {
    event.preventDefault(); event.stopPropagation();
    const p = this.confirmed.find(item => item.player.id === this.dragged);
    this.dragged = null;
    if (p) this.move(p, jersey, position);
  }
  private run(request: Observable<any>, balance = false): void {
    this.busy = true; this.error = ''; this.result = null;
    const id = this.event.id, account = this.players.getCurrentPlayer()?.id;
    request.pipe(takeUntil(this.destroyed)).subscribe({
      next: result => {
        if (this.event.id !== id || this.players.getCurrentPlayer()?.id !== account) { this.busy = false; return; }
        this.reload(balance ? result : null);
      },
      error: error => {
        this.busy = false;
        if (error?.status === 409 && error?.error?.code === 'PARTICIPATION_CHANGED') {
          this.error = 'guestOwner.notices.participationChangedMessage';
          this.refreshRequired = true;
          return;
        }
        this.error = error?.status === 403 ? 'errors.balanceTeamsManagerOnly'
          : error?.status === 404 ? 'errors.balanceTeamsEventMissing'
          : balance && error?.status === 400 ? 'errors.balanceTeamsMinPlayers' : 'errors.balanceTeamsGeneric';
      }
    });
  }
  reload(result: BalanceTeamsResult = null): void {
    if (!this.event?.id) return;
    this.busy = true;
    const id = this.event.id;
    const account = this.players.getCurrentPlayer()?.id;
    this.roster.fetchForEvent(id).pipe(takeUntil(this.destroyed)).subscribe({
      next: roster => {
        if (this.event.id !== id || this.players.getCurrentPlayer()?.id !== account) { this.busy = false; return; }
        this.event.playerEventInfos = roster;
        this.ngOnChanges(); this.result = result;
        this.refreshRequired = false; this.busy = false; this.error = '';
        this.rosterChanged.emit(roster);
      },
      error: () => { this.busy = false; this.refreshRequired = true; this.error = 'events.teamBoardRefreshRequired'; }
    });
  }
}
